"use client";

import { c as _c } from "react/compiler-runtime";
import { BurgerMenuMobile, CloseIcon } from "@local/ui/src/components/ui/icons";
import { useMenuLayout } from "../../../../lib/useMenuLayout";
import { Fragment, useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";
import { cn } from "@local/utils/src/cn";
import { usePathname, useParams } from "next/navigation";
import { getT } from "@local/i18n/src/t";
import { getLanguageForMarket, localizeMarketPath } from "@local/i18n/src/languages";
import { a11y } from "@local/i18n/src/catalogs/a11y";
import { visibleNavItems } from "../navVisibility";
import type { NavigationMainItem } from "../types";
import type { Route } from "next";
import { Link } from "@local/ui/src/components/ui/Link";
import { AnchorNavLink } from "./AnchorNavLink";
import { Modal } from "@local/ui/src/components/ui/Modal";
import dynamic from "next/dynamic";

// Dynamically import static page components for modal display
const AboutUs = dynamic(() => import("../../StaticPages/AboutUs").then(mod => mod.AboutUs), {
  ssr: false
});
const VeteransCharities = dynamic(() => import("../../StaticPages/VeteransCharities").then(mod => mod.VeteransCharities), {
  ssr: false
});
const BaerskinStories = dynamic(() => import("../../StaticPages/BaerskinStories").then(mod => mod.BaerskinStories), {
  ssr: false
});
const PrivacyPolicy = dynamic(() => import("../../StaticPages/PrivacyPolicy").then(mod => mod.PrivacyPolicy), {
  ssr: false
});
const TermsConditions = dynamic(() => import("../../StaticPages/TermsConditions").then(mod => mod.TermsConditions), {
  ssr: false
});
const ShippingReturnsRefunds = dynamic(() => import("../../StaticPages/ShippingReturnsRefunds").then(mod => mod.ShippingReturnsRefunds), {
  ssr: false
});
const SecurePayments = dynamic(() => import("../../StaticPages/SecurePayments").then(mod => mod.SecurePayments), {
  ssr: false
});
const Accessibility = dynamic(() => import("../../StaticPages/Accessibility").then(mod => mod.Accessibility), {
  ssr: false
});
const MOBILE_NAV_DRAWER_ID = "mobile-nav-drawer";
interface Props {
  market: string;
  navigationMain?: NavigationMainItem[];
}
interface MenuItemProps {
  item: NavigationMainItem;
  setOpen: (target?: boolean | undefined) => void;
  market: string;
}
interface SubMenuItemProps {
  item: NavigationMainItem;
  setOpen: (target?: boolean | undefined) => void;
  index: number;
  market: string;
}

/**
 * Determines if a URL should open in a modal instead of navigating
 */
const shouldOpenModal = (url?: string) => {
  if (!url) return false;
  return url.includes("about-us") || url.includes("our-story") || url.includes("privacy-policy") || url.includes("terms-and-conditions") || url.includes("shipping-returns-and-refunds") || url.includes("secure-payments") || url.includes("veterans-charities") || url.includes("baerskin-stories") || url.includes("accessibility");
};

/**
 * Gets the appropriate modal content based on URL
 */
const getModalContent = (market: string, url?: string) => {
  if (!url) return null;
  switch (true) {
    case url.includes("about-us") || url.includes("our-story"):
      return <AboutUs market={market} />;
    case url.includes("privacy-policy"):
      return <PrivacyPolicy market={market} />;
    case url.includes("terms-and-conditions"):
      return <TermsConditions market={market} />;
    case url.includes("shipping-returns-and-refunds"):
      return <ShippingReturnsRefunds market={market} />;
    case url.includes("secure-payments"):
      return <SecurePayments market={market} />;
    case url.includes("veterans-charities"):
      return <VeteransCharities market={market} />;
    case url.includes("baerskin-stories"):
      return <BaerskinStories market={market} />;
    case url.includes("accessibility"):
      return <Accessibility market={market} />;
    default:
      return null;
  }
};
export const HeaderMenuMobileButton = () => {
  const $ = _c(14);
  const {
    toggleOpen: toggleMenu,
    isOpen: open
  } = useMenuLayout();
  const pathname = usePathname();
  const params = useParams();
  const t = getT(getLanguageForMarket(params?.market), {
    a11y
  });
  let t0;
  if ($[0] !== toggleMenu) {
    t0 = () => {
      toggleMenu(false);
    };
    $[0] = toggleMenu;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== pathname || $[3] !== toggleMenu) {
    t1 = [pathname, toggleMenu];
    $[2] = pathname;
    $[3] = toggleMenu;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  useEffect(t0, t1);
  const t2 = "button";
  const t3 = "-ml-1 aspect-square h-full p-1 sm:ml-1 xl:hidden";
  let t4;
  if ($[5] !== open || $[6] !== toggleMenu) {
    t4 = () => toggleMenu(!open);
    $[5] = open;
    $[6] = toggleMenu;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = MOBILE_NAV_DRAWER_ID;
  const t6 = open ? t("a11y.closeMenu") : t("a11y.openMenu");
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex justify-center text-sm md:text-xs" aria-hidden="true"><BurgerMenuMobile /></div>;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== open || $[10] !== t4 || $[11] !== t5 || $[12] !== t6) {
    t8 = <button type={t2} className={t3} onClick={t4} aria-expanded={open} aria-controls={t5} aria-label={t6}>{t7}</button>;
    $[9] = open;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
};
const MenuSubItemMobile = t0 => {
  const $ = _c(50);
  const {
    item,
    index,
    market,
    setOpen
  } = t0;
  const [modalOpen, setModalOpen] = useState(false);
  let t1;
  if ($[0] !== item.url || $[1] !== market) {
    t1 = getModalContent(market, item.url);
    $[0] = item.url;
    $[1] = market;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const modalContent = t1;
  if (!item.subitems || item.subitems.length === 0) {
    if (shouldOpenModal(item.url)) {
      const t2 = `mobile-featured-heading-${item.id}-${index}`;
      let t3;
      if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = () => {
          setModalOpen(true);
        };
        $[3] = t3;
      } else {
        t3 = $[3];
      }
      let t4;
      if ($[4] !== item.name || $[5] !== t2) {
        t4 = <button id={t2} className="flex flex-row justify-between font-medium text-gray-100 hover:opacity-75" onClick={t3} type="button">{item.name}</button>;
        $[4] = item.name;
        $[5] = t2;
        $[6] = t4;
      } else {
        t4 = $[6];
      }
      let t5;
      if ($[7] !== modalContent || $[8] !== modalOpen) {
        t5 = <Modal open={modalOpen} toggle={setModalOpen} contentClassName="max-w-4xl">{modalContent}</Modal>;
        $[7] = modalContent;
        $[8] = modalOpen;
        $[9] = t5;
      } else {
        t5 = $[9];
      }
      let t6;
      if ($[10] !== t4 || $[11] !== t5) {
        t6 = <>{t4}{t5}</>;
        $[10] = t4;
        $[11] = t5;
        $[12] = t6;
      } else {
        t6 = $[12];
      }
      return t6;
    }
    if (item.anchor) {
      const t2 = `mobile-featured-heading-${item.id}-${index}`;
      let t3;
      if ($[13] !== setOpen) {
        t3 = () => setOpen(false);
        $[13] = setOpen;
        $[14] = t3;
      } else {
        t3 = $[14];
      }
      let t4;
      if ($[15] !== item.anchor || $[16] !== item.name || $[17] !== t2 || $[18] !== t3) {
        t4 = <AnchorNavLink anchor={item.anchor} id={t2} className="flex flex-row justify-between font-medium text-gray-100 hover:opacity-75" onNavigate={t3}>{item.name}</AnchorNavLink>;
        $[15] = item.anchor;
        $[16] = item.name;
        $[17] = t2;
        $[18] = t3;
        $[19] = t4;
      } else {
        t4 = $[19];
      }
      return t4;
    }
    const t2 = `mobile-featured-heading-${item.id}-${index}`;
    let t3;
    if ($[20] !== item.url || $[21] !== market) {
      t3 = (item.url && localizeMarketPath(item.url, market)) ?? "#";
      $[20] = item.url;
      $[21] = market;
      $[22] = t3;
    } else {
      t3 = $[22];
    }
    const t4 = t3 as Route;
    let t5;
    if ($[23] !== setOpen) {
      t5 = () => setOpen(false);
      $[23] = setOpen;
      $[24] = t5;
    } else {
      t5 = $[24];
    }
    let t6;
    if ($[25] !== item.name || $[26] !== t2 || $[27] !== t4 || $[28] !== t5) {
      t6 = <Link id={t2} className="flex flex-row justify-between font-medium text-gray-100 hover:opacity-75" href={t4} prefetch={false} onClick={t5}>{item.name}</Link>;
      $[25] = item.name;
      $[26] = t2;
      $[27] = t4;
      $[28] = t5;
      $[29] = t6;
    } else {
      t6 = $[29];
    }
    return t6;
  }
  if (!item.url) {
    return null;
  }
  let t2;
  if ($[30] !== item.url || $[31] !== market) {
    t2 = localizeMarketPath(item.url, market);
    $[30] = item.url;
    $[31] = market;
    $[32] = t2;
  } else {
    t2 = $[32];
  }
  const t3 = t2 as Route;
  const t4 = `mobile-featured-heading-${item.id}-${index}`;
  const t5 = `mobile-list-heading-${item.id}`;
  let t6;
  if ($[33] !== item.name || $[34] !== t5) {
    t6 = <span id={t5}>{item.name}</span>;
    $[33] = item.name;
    $[34] = t5;
    $[35] = t6;
  } else {
    t6 = $[35];
  }
  let t7;
  if ($[36] !== t3 || $[37] !== t4 || $[38] !== t6) {
    t7 = <Link href={t3} id={t4} className="flex flex-row justify-between font-medium text-gray-100 hover:opacity-75" prefetch={false}>{t6}</Link>;
    $[36] = t3;
    $[37] = t4;
    $[38] = t6;
    $[39] = t7;
  } else {
    t7 = $[39];
  }
  const t8 = `mobile-list-heading-${item.id}`;
  let t9;
  if ($[40] !== item.id || $[41] !== item.subitems || $[42] !== market) {
    t9 = item.subitems?.map((subitem, i) => <li key={`menu-item-mobile-${subitem.id}-${i}`} className="flex">{subitem.url && <Link id={`menu-item-mobile-client-${item.id}-${i}`} href={localizeMarketPath(subitem.url, market) as Route} className="text-gray-400" prefetch={false}>{subitem.name}</Link>}</li>);
    $[40] = item.id;
    $[41] = item.subitems;
    $[42] = market;
    $[43] = t9;
  } else {
    t9 = $[43];
  }
  let t10;
  if ($[44] !== t8 || $[45] !== t9) {
    t10 = <ul aria-labelledby={t8} className="space-y-2">{t9}</ul>;
    $[44] = t8;
    $[45] = t9;
    $[46] = t10;
  } else {
    t10 = $[46];
  }
  let t11;
  if ($[47] !== t10 || $[48] !== t7) {
    t11 = <Fragment>{t7}{t10}</Fragment>;
    $[47] = t10;
    $[48] = t7;
    $[49] = t11;
  } else {
    t11 = $[49];
  }
  return t11;
};
const MenuItemMobile = t0 => {
  const $ = _c(4);
  const {
    item,
    setOpen,
    market
  } = t0;
  let t1;
  if ($[0] !== item || $[1] !== market || $[2] !== setOpen) {
    t1 = <div className="space-y-4 px-4 pt-10"><div className="grid grid-cols-1 items-start gap-x-4 gap-y-4"><div className="grid grid-cols-1 gap-4"><MenuSubItemMobile index={0} item={item} market={market} setOpen={setOpen} /></div></div></div>;
    $[0] = item;
    $[1] = market;
    $[2] = setOpen;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
export const HeaderMenuMobile = t0 => {
  const $ = _c(30);
  const {
    market,
    navigationMain
  } = t0;
  const {
    toggleOpen: toggleMenu,
    isOpen: open
  } = useMenuLayout();
  const t = getT(getLanguageForMarket(market), {
    a11y
  });
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);
  const previousFocusRef = useRef(null);
  const filteredNavigation = visibleNavItems(navigationMain, market);
  let t1;
  let t2;
  if ($[0] !== open) {
    t1 = () => {
      if (!open) {
        return;
      }
      previousFocusRef.current = document.activeElement as HTMLElement | null;
      requestAnimationFrame(() => {
        closeButtonRef.current?.focus();
      });
      return () => {
        if (previousFocusRef.current) {
          previousFocusRef.current.focus();
          previousFocusRef.current = null;
        }
      };
    };
    t2 = [open];
    $[0] = open;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== open || $[4] !== toggleMenu) {
    t3 = () => {
      if (!open) {
        return;
      }
      const handleEscape = event => {
        if (event.key === "Escape") {
          toggleMenu(false);
        }
      };
      document.addEventListener("keydown", handleEscape);
      return () => {
        document.removeEventListener("keydown", handleEscape);
      };
    };
    t4 = [open, toggleMenu];
    $[3] = open;
    $[4] = toggleMenu;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useEffect(t3, t4);
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = e => {
      if (e.key !== "Tab" || !dialogRef.current) {
        return;
      }
      const focusable = dialogRef.current.querySelectorAll("button, [href], input, select, textarea, [tabindex]:not([tabindex=\"-1\"])");
      if (focusable.length === 0) {
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const handleKeyDown = t5;
  const t6 = open ? "opacity-100" : "opacity-0";
  let t7;
  if ($[8] !== t6) {
    t7 = cn("relative z-240 xl:hidden", t6);
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      transition: "opacity linear 300ms"
    };
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const t9 = open ? "visible block" : "invisible hidden";
  let t10;
  if ($[11] !== t9) {
    t10 = cn("fixed inset-0 z-50 flex", t9, "w-full bg-gray-600/50 backdrop-blur-xs");
    $[11] = t9;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== toggleMenu) {
    t11 = () => toggleMenu(false);
    $[13] = toggleMenu;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t10 || $[16] !== t11) {
    t12 = <div className={t10} role="presentation" onClick={t11} />;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const t13 = open ? "translate-x-0" : "-translate-x-full";
  let t14;
  if ($[18] !== t13) {
    t14 = cn("fixed inset-0 z-50 flex", t13);
    $[18] = t13;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      transition: "transform ease-in-out 300ms"
    };
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  const t16 = t("a11y.mainMenu");
  const t17 = t("a11y.closeMenu");
  let t18;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = cn("-m-2 inline-flex items-center justify-center rounded-md p-2 text-gray-400");
    $[21] = t18;
  } else {
    t18 = $[21];
  }
  let t19;
  if ($[22] !== toggleMenu) {
    t19 = () => toggleMenu(false);
    $[22] = toggleMenu;
    $[23] = t19;
  } else {
    t19 = $[23];
  }
  let t20;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <CloseIcon aria-hidden="true" />;
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  const t21 = "mt-2";
  const t22 = t("a11y.mobileMenu");
  const t23 = filteredNavigation?.map((item, i) => <MenuItemMobile key={`menu-mobile-${i}`} item={item} setOpen={toggleMenu} market={market} />);
  let t24;
  if ($[25] !== t22 || $[26] !== t23) {
    t24 = <nav className={t21} aria-label={t22}>{t23}</nav>;
    $[25] = t22;
    $[26] = t23;
    $[27] = t24;
  } else {
    t24 = $[27];
  }
  let t25;
  if ($[28] !== toggleMenu) {
    t25 = <div className="h-full w-full" role="presentation" onClick={() => toggleMenu(false)} />;
    $[28] = toggleMenu;
    $[29] = t25;
  } else {
    t25 = $[29];
  }
  return <div className={t7} style={t8} inert={!open ? true : undefined}>{t12}<div className={t14} style={t15}><div ref={dialogRef} id={MOBILE_NAV_DRAWER_ID} role="dialog" aria-modal="true" aria-label={t16} tabIndex={-1} onKeyDown={handleKeyDown} className="absolute left-0 flex h-full w-full max-w-xs flex-col overflow-y-auto bg-gray-950 pb-12 text-white shadow-xl md:max-w-sm lg:max-w-md"><div className="flex px-4 pt-5 pb-2"><button ref={closeButtonRef} type="button" aria-label={t17} className={t18} onClick={t19}><span className="sr-only">{t("a11y.closeMenu")}</span>{t20}</button></div>{t24}</div>{t25}</div></div>;
};